import { BLEND_MODES, Circle, Container, Graphics, InteractionEvent, InteractionManager, LINE_CAP, LINE_JOIN, Point, Rectangle, Text } from "pixi.js"
import { getRendererApp } from "../renderer_app"
import { TextRenderer } from "../text/text_renderer"
import { trackLetters } from "../text/typography"
import { chamfer, drawBoard, GOLD, INK, MUTED, PLOT_FACE, PLOT_RIM, RIM, RIM_LIGHT, TIMBER_EDGE } from "./build_board"
import { diamond, filledSocket, glowDisc, shineBand, spark, sparkBurst, REWARD_GOLD, REWARD_GOLD_HOT } from "./chapter_reward_art"
import { easeInCubic, easeInOutCubic, easeOutBack, easeOutCubic, lerp, lerpColor, span, stampPose } from "./chapter_reward_motion"
import { getUiMetrics } from "./ui_metrics"

// The window every animated helper is played in: a timber board with a gold spark and an overline,
// a step track that lights each step as the demonstration reaches it and stamps it once it is done,
// a diorama of the camp set into the board, a pointer that works the real controls inside it, a
// caption under it, and Replay once a play-through has finished. The building helper
// (building_tutorial.ts) was the first; the forager's wheel (forager_tutorial.ts) is the second.
//
// A subclass owns what is INSIDE the diorama -- it adds its scene to `content` and poses it in
// poseScene -- and hands this class the timeline's few facts: the steps, the caption at any instant
// and where the pointer is. Everything else is here, so every helper arrives, drags, replays and
// leaves alike.
//
// A helper played a step at a time is worked from the caption row, beside the words it is pacing:
// a small Back plate and a Next plate that lights up once its step has played and is waiting on the
// player, which hands its place to Replay at the end. The steps on the track can be picked too.
// Display caps are tracked with trackLetters: pixi's letterSpacing measures but does not draw here.

const OVERLINE_Y = 15
const TITLE_Y = 27
// The step track, between the title and the scene.
const TRACK_Y = 72
const NODE_R = 6.5
const NODE_LABEL_GAP = 6
const LINK_GAP = 8
const STEP_LIGHT_MS = 260

// The close X: bare, like the clue notes' (clue_callout.ts), so the loudest thing on the board is
// never the way out of it.
const CLOSE_ARM = 3.75
const CLOSE_HIT = 12

// The caption row's controls: the action plate (Next, then Replay), and the square Back plate left
// of it. The caption wraps short of whatever the row holds.
const ACTION_W = 78
const ACTION_H = 24
const ACTION_GAP = 6
const CAPTION_GAP = 10
const ACTION_IN_MS = 220
// Next lights over this long once its step has played; then it stays still, never breathing.
const NEXT_LIGHT_MS = 320

// Arrival and departure: set down onto the screen, lifted away -- never a dialog popping.
const ENTER_MS = 320, ENTER_RISE = 12
const LEAVE_MS = 170, LEAVE_DROP = 6

const CAPTION_IN_MS = 240
const DONE_TINT = 0xffe2a0

const RIPPLE_MS = 460

/**
 * What the player's own press did to a helper window: closed it, picked a step (Back, Next or one on
 * the track), or played it again. None of these sends anything the game needs, so each is reported
 * (TutorialDemo.onPlayerUse) for the session recording, and a replay acts it out the same way.
 */
export type TutorialUse = { action: "close" } | { action: "step"; step: number } | { action: "replay" }

/** The steps along the track, and when each lights, is checked off and hands on to the next. */
export interface DemoSteps {
    labels: string[]
    litAt: number[]
    doneAt: number[]
    // One [from, to] per link: when the line to the next step fills.
    linkFill: [number, number][]
}

export interface DemoLayout {
    width: number
    height: number
    // The left edge the header, track and caption share, and the width the track and caption span.
    padX: number
    contentWidth: number
    captionY: number
    captionH: number
    overline: string
    title: string
    steps: DemoSteps
    // One play-through, then it holds on its last frame until Replay, which shows from replayAt.
    durationMs: number
    replayAt: number
    /** When supplied, each scene holds before the next start until the player presses Next. */
    manualStepStarts?: number[]
}

export type Pt = { x: number; y: number }

/** Where the pointer is, whether it is held down, how visible it is. */
export interface DemoCursor extends Pt {
    pressed: boolean
    alpha: number
}

/** The plaque that slams in over a finished result, `ms` after it starts. Settled within 1.1s. */
export const raisedStampPose = (ms: number) => ({
    alpha: ms < 0 ? 0 : easeOutCubic(span(ms, 0, 120)),
    scale: ms < 0 ? 0 : lerp(1.9, 1, easeOutBack(span(ms, 0, 320), 2.2)),
    checkDraw: easeOutCubic(span(ms, 90, 380)),
    burstT: span(ms, 0, 620),
    shineT: ms < 0 ? -1 : easeInOutCubic(span(ms, 340, 1000)),
})

const cursorArrow = [0, 0, 0, 13.5, 3.3, 10.4, 5.7, 15.6, 7.9, 14.6, 5.6, 9.6, 9.9, 9.6]

/**
 * The diorama is set INTO the board: a timber casing with chamfered inner corners, shadowed
 * along the top where the board overhangs it and lit along the bottom lip, and the grass
 * darkening toward the edges so the eye stays on the middle where the lesson happens.
 */
export const drawDioramaFrame = (g: Graphics, x: number, y: number, w: number, h: number) => {
    // The vignette: stacked frames with ever-smaller holes, equal alpha each, so the falloff is
    // their accumulation -- darkest at the casing, gone a few cells in.
    for (let i = 1; i <= 8; i++) {
        const d = i * 2.5
        g.beginFill(0x000000, 0.045)
        g.drawRect(x, y, w, h)
        g.beginHole()
        g.drawRect(x + d, y + d, w - d * 2, h - d * 2)
        g.endHole()
        g.endFill()
    }
    // Inner shadow under the top lip.
    for (let i = 0; i < 4; i++) {
        g.lineStyle(1, 0x000000, 0.34 - i * 0.075).moveTo(x, y + 0.5 + i).lineTo(x + w, y + 0.5 + i)
    }
    // The casing: a band with chamfered inner corners, covering the scene's own square corners.
    g.lineStyle(0)
    g.beginFill(TIMBER_EDGE, 1)
    g.drawRect(x - 3, y - 3, w + 6, h + 6)
    g.beginHole()
    chamfer(g, x, y, w, h, 5)
    g.endHole()
    g.endFill()
    g.lineStyle({ width: 1, color: RIM, alpha: 0.75, join: LINE_JOIN.MITER })
    chamfer(g, x - 0.5, y - 0.5, w + 1, h + 1, 5.5)
    g.lineStyle(1, RIM_LIGHT, 0.22).moveTo(x + 6, y + h + 1.5).lineTo(x + w - 6, y + h + 1.5)
}

/** Gold rings where the pointer clicked, each `at` ms into the play-through. */
export const drawRipples = (g: Graphics, t: number, times: number[], cursorAt: (ms: number) => Pt) => {
    times.forEach(at => {
        const k = span(t, at, at + RIPPLE_MS)
        if (t < at || k >= 1) return
        const p = cursorAt(at)
        g.lineStyle(2, REWARD_GOLD, 0.9 * Math.pow(1 - k, 1.5))
        g.drawCircle(p.x, p.y, lerp(3, 16, easeOutCubic(k)))
        g.lineStyle(0)
        glowDisc(g, p.x, p.y, 9, REWARD_GOLD, 0.45 * (1 - k), 3)
    })
}

/** The plaque that names what the demonstration just did ("WALL RAISED"), slammed in at its end. */
export class DemoStamp {
    readonly plaque = new Container()
    // The plaque's burst, squashed flat so it fans out along the free row instead of up into the
    // step track.
    readonly burst = new Graphics()
    private plate = new Graphics()
    private shine = new Graphics()
    private size = { w: 0, h: 24 }

    constructor(label: string) {
        const text = new TextRenderer().fontSize(11).bold().strokeThickness(0).render(trackLetters(label)) as Text
        text.anchor.set(0, 0.5)
        let width = 0
        try { text.updateText(true); width = text.width } catch { /* fallback below */ }
        width = Math.max(width, 80)
        const w = Math.round(width + 46)
        this.size.w = w
        text.position.set(-w / 2 + 31, 0.5)
        text.tint = REWARD_GOLD
        this.shine.blendMode = BLEND_MODES.ADD
        this.plaque.addChild(this.plate, text, this.shine)
        this.plaque.visible = false
        this.burst.blendMode = BLEND_MODES.ADD
        this.burst.scale.set(1, 0.4)
    }

    place = (x: number, y: number) => {
        this.plaque.position.set(x, y)
        this.burst.position.set(x, y)
    }

    /** Pose it `ms` after it starts (negative: not yet). */
    draw = (ms: number) => {
        this.plaque.visible = ms >= 0
        this.burst.clear()
        if (ms < 0) return
        const pose = raisedStampPose(ms)
        this.plaque.alpha = pose.alpha
        this.plaque.scale.set(pose.scale)
        const { w, h } = this.size
        const plate = this.plate.clear()
        // Dropped straight down, then the cut-timber plate with a gold rim over a dark casing.
        plate.beginFill(0x000000, 0.4); chamfer(plate, -w / 2, -h / 2 + 3, w, h, 5); plate.endFill()
        plate.lineStyle({ width: 3.5, color: TIMBER_EDGE, alpha: 1, join: LINE_JOIN.MITER })
        plate.beginFill(0x1b1512, 0.97); chamfer(plate, -w / 2, -h / 2, w, h, 5); plate.endFill()
        plate.lineStyle({ width: 1.25, color: REWARD_GOLD, alpha: 1, join: LINE_JOIN.MITER })
        chamfer(plate, -w / 2, -h / 2, w, h, 5)
        plate.lineStyle(1, RIM_LIGHT, 0.25).moveTo(-w / 2 + 6, -h / 2 + 2).lineTo(w / 2 - 6, -h / 2 + 2)
        plate.lineStyle(0)
        filledSocket(plate, -w / 2 + 17, 0, 7, pose.checkDraw)
        shineBand(this.shine.clear(), -w / 2, -h / 2, w, h, pose.shineT, 22, 0.75)
        sparkBurst(this.burst, 0, 0, pose.burstT, 14, w * 0.42, w * 0.78, REWARD_GOLD, 0.2, 1.6)
    }
}

type ActionGlyph = "next" | "back" | "replay"

/**
 * A plate on the caption row, cut like the stamp's: a dark face in a timber casing with a bronze rim.
 * `lit` (0..1, set by the window) warms the face, turns the rim gold and brightens the words, and
 * hovering lights it fully; at rest it is quiet but plainly a control.
 */
export class DemoAction extends Container {
    lit = 0
    private plate = new Graphics()
    private glyph = new Graphics()
    private text?: Text
    private hovered = false
    private pressed = false
    private drawn = ""

    constructor(private icon: ActionGlyph, private w: number, onTap: () => void, label?: string) {
        super()
        this.addChild(this.plate)
        // The glyph leads Replay, follows Next, and is all a Back plate holds; the pair is centred.
        const glyphW = 8, gap = 5
        let textW = 0
        if (label) {
            this.text = new TextRenderer().fontSize(10).bold().strokeThickness(0).render(trackLetters(label)) as Text
            this.text.anchor.set(0, 0.5)
            try { this.text.updateText(true); textW = this.text.width } catch { /* fallback below */ }
            textW = textW || label.length * 7.5
            this.addChild(this.text)
        }
        const left = (w - (label ? textW + gap + glyphW : glyphW)) / 2
        this.glyph.x = left + (icon === "next" ? textW + gap : 0) + glyphW / 2
        if (this.text) this.text.x = icon === "next" ? left : left + glyphW + gap
        this.addChild(this.glyph)
        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = new Rectangle(-3, -5, w + 6, ACTION_H + 10)
        this.on("pointerover", () => { this.hovered = true; this.draw() })
        this.on("pointerout", () => { this.hovered = this.pressed = false; this.draw() })
        this.on("pointerdown", () => { this.pressed = true; this.draw() })
        this.on("pointerup", () => { this.pressed = false; this.draw() })
        this.on("pointerupoutside", () => { this.pressed = false; this.draw() })
        this.on("pointertap", (event: InteractionEvent) => { event.stopPropagation(); onTap() })
        this.draw()
    }

    draw = () => {
        const k = this.hovered ? 1 : Math.max(0, Math.min(1, this.lit))
        const key = `${Math.round(k * 32)}:${this.hovered}:${this.pressed}`
        if (key === this.drawn) return
        this.drawn = key
        const { w } = this, h = ACTION_H, dy = this.pressed ? 1 : 0
        const g = this.plate.clear()
        // Dropped straight down; pressing closes the gap.
        g.beginFill(0x000000, 0.4); chamfer(g, 0, 3, w, h, 4); g.endFill()
        g.lineStyle({ width: 3, color: TIMBER_EDGE, alpha: 1, join: LINE_JOIN.MITER })
        g.beginFill(lerpColor(0x1b1512, 0x33240f, k), 0.97); chamfer(g, 0, dy, w, h, 4); g.endFill()
        g.lineStyle({ width: 1.25, color: this.hovered ? GOLD : lerpColor(RIM, REWARD_GOLD, k), alpha: 1, join: LINE_JOIN.MITER })
        chamfer(g, 0, dy, w, h, 4)
        g.lineStyle(1, RIM_LIGHT, 0.12 + 0.18 * k).moveTo(5, dy + 2).lineTo(w - 5, dy + 2)
        g.lineStyle(0)
        if (this.text) {
            this.text.y = h / 2 + dy
            this.text.tint = lerpColor(RIM_LIGHT, INK, k)
        }
        this.glyph.y = h / 2 + dy
        this.drawGlyph(lerpColor(RIM_LIGHT, REWARD_GOLD, k))
    }

    private drawGlyph = (color: number) => {
        const g = this.glyph.clear()
        g.lineStyle({ width: 1.75, color, alpha: 1, cap: LINE_CAP.ROUND, join: LINE_JOIN.ROUND })
        if (this.icon === "next") { g.moveTo(-2, -4).lineTo(2, 0).lineTo(-2, 4); return }
        if (this.icon === "back") { g.moveTo(2, -4).lineTo(-2, 0).lineTo(2, 4); return }
        // Replay: most of a turn clockwise, its head at the top right pointing back into the gap.
        const r = 4.25, from = Math.PI * 0.12, to = Math.PI * 1.72
        g.moveTo(Math.cos(from) * r, Math.sin(from) * r)
        for (let i = 1; i <= 14; i++) {
            const a = lerp(from, to, i / 14)
            g.lineTo(Math.cos(a) * r, Math.sin(a) * r)
        }
        g.lineStyle(0)
        const tip = { x: Math.cos(to) * r, y: Math.sin(to) * r }
        const along = { x: -Math.sin(to), y: Math.cos(to) }
        g.beginFill(color, 1).drawPolygon([
            tip.x + along.x * 3, tip.y + along.y * 3,
            tip.x - along.y * 2.6 - along.x * 0.8, tip.y + along.x * 2.6 - along.y * 0.8,
            tip.x + along.y * 2.6 - along.x * 0.8, tip.y - along.x * 2.6 - along.y * 0.8,
        ]).endFill()
    }
}

/** A helper window. Subclasses build their diorama into `content` and pose it in poseScene. */
export abstract class TutorialDemo extends Container {
    // Where the subclass's scene goes: above the board, header and track; below the pointer, the
    // caption and the window's own controls.
    protected content = new Container()
    protected elapsed = 0
    // The frame clock: runs on while a play-through holds on its last frame.
    protected clock = 0
    // A fingertip instead of the arrow, for a touch screen.
    protected touch = false
    private trackGlow = new Graphics()
    private track = new Graphics()
    private trackLabels: Text[] = []
    private trackX: { node: number; label: number; end: number }[] = []
    private pointer = new Graphics()
    private closeX = new Graphics()
    private replayButton: DemoAction
    private backButton?: DemoAction
    private nextButton?: DemoAction
    private manualStep = 0
    // When the current step finished playing and began waiting on Next (frame clock), and when the
    // caption row's plates were last brought in.
    private heldAt?: number
    private nextShownAt = 0
    private backShownAt = 0
    private replayShownAt = 0
    private hoveredStep = -1
    private stepTargets: Graphics[] = []
    private caption: Text
    private captionShownAt = 0
    private leavingAt?: number
    private closeHovered = false
    private hovered = false
    private manualPosition?: Point
    private drag?: { id: number; offset: Point; interaction?: InteractionManager }
    private titleHandle = new Graphics()
    /** Told each time the player's own press changes the window (see TutorialUse). */
    onPlayerUse?: (use: TutorialUse) => void

    constructor(protected layout: DemoLayout, private hover: (over: boolean) => void, private dismiss: () => void) {
        super()
        const { width, height, padX, captionY, captionH, contentWidth } = layout
        const board = new Graphics(); drawBoard(board, width, height); this.addChild(board)
        this.drawHeader()
        this.trackGlow.blendMode = BLEND_MODES.ADD
        this.addChild(this.trackGlow)
        this.addChild(this.track)
        this.buildTrackLabels()
        this.addChild(this.content)
        this.addChild(this.pointer)

        this.caption = new TextRenderer().fontSize(13).color(INK).strokeThickness(0).render("") as Text
        this.caption.style.wordWrap = true; this.caption.style.wordWrapWidth = contentWidth
        this.caption.position.set(padX, captionY); this.addChild(this.caption)
        // Next and Replay share the end of the caption row; Back sits just inside them.
        const actionX = width - padX - ACTION_W, actionY = captionY + (captionH - ACTION_H) / 2 - 2
        this.replayButton = new DemoAction("replay", ACTION_W, () => this.use({ action: "replay" }), "REPLAY")
        this.replayButton.position.set(actionX, actionY)
        this.replayButton.visible = false
        this.addChild(this.replayButton)
        if (layout.manualStepStarts) {
            this.backButton = new DemoAction("back", ACTION_H, () => this.use({ action: "step", step: this.manualStep - 1 }))
            this.nextButton = new DemoAction("next", ACTION_W, () => this.use({ action: "step", step: this.manualStep + 1 }), "NEXT")
            this.backButton.position.set(actionX - ACTION_GAP - ACTION_H, actionY)
            this.nextButton.position.set(actionX, actionY)
            this.addChild(this.backButton, this.nextButton)
            this.buildStepTargets()
        }

        this.titleHandle.beginFill(0xffffff, 0.001).drawRect(8, 6, width - 48, TRACK_Y - 16).endFill()
        this.titleHandle.interactive = true
        this.titleHandle.cursor = "grab"
        this.titleHandle.on("pointerdown", this.startDrag)
        this.titleHandle.on("pointermove", this.moveDrag)
        this.titleHandle.on("pointerup", this.endDrag)
        this.titleHandle.on("pointerupoutside", this.endDrag)
        this.titleHandle.on("pointercancel", this.endDrag)
        this.addChild(this.titleHandle)

        this.closeX.position.set(width - 22, 22)
        this.closeX.interactive = true
        this.closeX.cursor = "pointer"
        this.closeX.hitArea = new Circle(0, 0, CLOSE_HIT)
        this.closeX.on("pointerover", () => { this.closeHovered = true; this.drawClose() })
        this.closeX.on("pointerout", () => { this.closeHovered = false; this.drawClose() })
        this.closeX.on("pointertap", (event: InteractionEvent) => { event.stopPropagation(); this.use({ action: "close" }) })
        this.drawClose()
        this.addChild(this.closeX)

        this.interactive = true
        this.on("mouseover", () => { this.hovered = true; this.hover(true) })
        this.on("mouseout", () => { if (!this.drag) { this.hovered = false; this.hover(false) } })
        this.on("pointerdown", event => event.stopPropagation())
        // The subclass calls animate(0) once its scene exists.
    }

    // ---- What the subclass supplies -------------------------------------------------------------

    /** Pose everything in the diorama at `t` ms into the play-through. */
    protected abstract poseScene(t: number, deltaMs: number): void
    /** The pointer at `t`. */
    protected abstract cursorAt(t: number): DemoCursor
    /** The caption at `t`; `done` once the demonstration has made its point. */
    protected abstract captionAt(t: number): { text: string; done: boolean }
    /** Put back whatever the play-through changed, before Replay starts it again. */
    protected abstract rewind(): void
    /** Last things to let go of, before the children are destroyed. */
    protected teardown() { /* nothing by default */ }

    // ---- Construction ---------------------------------------------------------------------------

    private drawHeader = () => {
        const { padX } = this.layout
        const sparkG = new Graphics()
        sparkG.beginFill(GOLD, 1); spark(sparkG, padX + 3.5, OVERLINE_Y + 5.5, 3.5); sparkG.endFill()
        this.addChild(sparkG)
        const overline = new TextRenderer().role("compactCaption").bold().strokeThickness(0).color(RIM_LIGHT)
            .render(trackLetters(this.layout.overline)) as Text
        overline.position.set(padX + 13, OVERLINE_Y)
        this.addChild(overline)
        const title = new TextRenderer().fontSize(18).bold().strokeThickness(0).color(INK).render(this.layout.title) as Text
        title.position.set(padX, TITLE_Y)
        this.addChild(title)
    }

    private buildTrackLabels = () => {
        const { labels } = this.layout.steps
        const widths = labels.map(label => {
            const text = new TextRenderer().fontSize(9).bold().strokeThickness(0).render(trackLetters(label)) as Text
            text.anchor.set(0, 0.5)
            text.y = TRACK_Y
            this.trackLabels.push(text)
            this.addChild(text)
            // A headless canvas measures nothing; never let the track collapse onto itself.
            let width = 0
            try { text.updateText(true); width = text.width } catch { /* measured below */ }
            return Math.max(width, label.length * 6.5)
        })
        // Diamond, its label, then a link reaching to the next diamond; the links share what is left.
        const fixed = widths.reduce((sum, w) => sum + NODE_R * 2 + NODE_LABEL_GAP + w, 0)
        const links = Math.max(1, labels.length - 1)
        const link = Math.max(12, (this.layout.contentWidth - fixed - LINK_GAP * links * 2) / links)
        let x = this.layout.padX
        widths.forEach((w, i) => {
            const node = x + NODE_R
            const label = x + NODE_R * 2 + NODE_LABEL_GAP
            this.trackLabels[i].x = label
            x = label + w + LINK_GAP
            this.trackX.push({ node, label, end: x })
            x += link + LINK_GAP
        })
    }

    /** Each step on the track, diamond and label, picks that step: a way back to any of them. */
    private buildStepTargets = () => {
        this.trackX.forEach(({ node, end }, i) => {
            const target = new Graphics()
            const left = node - NODE_R - 4
            target.beginFill(0xffffff, 0.001).drawRect(left, TRACK_Y - 11, end - LINK_GAP + 4 - left, 22).endFill()
            target.interactive = true
            target.cursor = "pointer"
            target.on("pointerover", () => { this.hoveredStep = i })
            target.on("pointerout", () => { if (this.hoveredStep === i) this.hoveredStep = -1 })
            target.on("pointertap", (event: InteractionEvent) => { event.stopPropagation(); this.use({ action: "step", step: i }) })
            this.stepTargets.push(target)
            this.addChild(target)
        })
    }

    private drawClose = () => {
        const g = this.closeX.clear()
        g.lineStyle({ width: 1.8, color: this.closeHovered ? GOLD : MUTED, alpha: this.closeHovered ? 1 : 0.85,
            cap: LINE_CAP.ROUND })
        g.moveTo(-CLOSE_ARM, -CLOSE_ARM).lineTo(CLOSE_ARM, CLOSE_ARM)
        g.moveTo(CLOSE_ARM, -CLOSE_ARM).lineTo(-CLOSE_ARM, CLOSE_ARM)
        g.lineStyle(0)
    }

    // ---- Window -------------------------------------------------------------------------------

    private replay = () => {
        this.manualStep = 0
        this.elapsed = 0
        this.heldAt = undefined
        this.rewind()
        this.animate(0)
    }

    /** Play `step` from its start (picking the step already showing plays it again). */
    private goToStep = (step: number): boolean => {
        const starts = this.layout.manualStepStarts
        if (!starts || !Number.isInteger(step) || step < 0 || step >= starts.length) return false
        this.manualStep = step
        this.elapsed = starts[step]
        this.heldAt = undefined
        this.rewind()
        this.animate(0)
        return true
    }

    /** Fade away and dismiss, as the close X does. False when it is already on its way out. */
    leave = (): boolean => {
        if (this.leavingAt !== undefined) return false
        this.leavingAt = this.clock
        return true
    }

    /** A press of the player's own, reported when it changed something. */
    private use = (use: TutorialUse) => {
        if (this.actOut(use)) this.onPlayerUse?.(use)
    }

    /** Do what a press did, the player's or (in a session replay) the recorded player's. */
    actOut = (use: TutorialUse): boolean => {
        if (use.action === "close") return this.leave()
        if (use.action === "step") return this.goToStep(use.step)
        this.replay()
        return true
    }

    private startDrag = (event: InteractionEvent) => {
        if (this.drag || !this.parent || (event.data.button ?? 0) !== 0) return
        event.stopPropagation()
        const at = this.parent.toLocal(event.data.global)
        let interaction: InteractionManager | undefined
        try { interaction = getRendererApp().renderer.plugins.interaction } catch { /* Detached previews can use the title's events. */ }
        this.drag = { id: event.data.pointerId, offset: new Point(at.x - this.x, at.y - this.y), interaction }
        this.manualPosition = this.position.clone()
        this.titleHandle.cursor = "grabbing"
        this.hovered = true; this.hover(true)
        interaction?.on("pointermove", this.moveDrag)
        interaction?.on("pointerup", this.endDrag)
        interaction?.on("pointerupoutside", this.endDrag)
        interaction?.on("pointercancel", this.endDrag)
    }

    private moveDrag = (event: InteractionEvent) => {
        if (!this.drag || !this.parent || event.data.pointerId !== this.drag.id) return
        event.stopPropagation()
        const at = this.parent.toLocal(event.data.global)
        this.manualPosition.set(at.x - this.drag.offset.x, at.y - this.drag.offset.y)
        this.placeWindow()
    }

    private endDrag = (event?: InteractionEvent) => {
        if (!this.drag || event && event.data.pointerId !== this.drag.id) return
        const interaction = this.drag.interaction
        interaction?.off("pointermove", this.moveDrag)
        interaction?.off("pointerup", this.endDrag)
        interaction?.off("pointerupoutside", this.endDrag)
        interaction?.off("pointercancel", this.endDrag)
        this.drag = undefined
        this.titleHandle.cursor = "grab"
        this.hovered = !!event?.data?.global && this.getBounds().contains(event.data.global.x, event.data.global.y)
        this.hover(this.hovered)
    }

    private placeWindow = () => {
        const { width, height } = this.layout
        const safe = getUiMetrics().safeViewport
        const scale = Math.min(1, Math.max(0.5, (safe.width - 24) / width), Math.max(0.5, (safe.height - 100) / height))
        this.scale.set(scale)
        const desired = this.manualPosition || new Point(safe.right - width * scale - 12,
            safe.top + Math.max(60, (safe.height - height * scale) / 2))
        const x = Math.max(safe.left, Math.min(desired.x, Math.max(safe.left, safe.right - width * scale)))
        const y = Math.max(safe.top, Math.min(desired.y, Math.max(safe.top, safe.bottom - height * scale)))
        this.position.set(x, y)
        this.manualPosition?.set(x, y)
    }

    // ---- The frame ----------------------------------------------------------------------------

    animate = (deltaMs: number) => {
        if (this.destroyed) return
        const step = Math.max(0, deltaMs || 0)
        this.clock += step
        // Arrival and departure ride on the PIVOT, so the position dragging reads stays clean.
        if (this.leavingAt !== undefined) {
            const k = easeInCubic(span(this.clock - this.leavingAt, 0, LEAVE_MS))
            this.alpha = 1 - k
            this.pivot.y = -LEAVE_DROP * k
            if (k >= 1) { this.dismiss(); return }
        } else {
            const k = span(this.clock, 0, ENTER_MS)
            this.alpha = easeOutCubic(Math.min(1, k * 1.6))
            this.pivot.y = -ENTER_RISE * (1 - easeOutBack(k, 1.4))
        }
        const starts = this.layout.manualStepStarts
        const stop = starts?.[this.manualStep + 1]
        this.elapsed = Math.min(stop === undefined ? this.layout.durationMs : stop - 1, this.elapsed + step)
        const t = this.elapsed
        this.poseScene(t, deltaMs)
        this.drawTrack(t)
        this.drawPointer(this.cursorAt(t))
        this.updateCaption(this.captionAt(t))
        this.poseActions(t, stop)
        this.placeWindow()
    }

    /**
     * Back once there is a step to go back to; Next until the last step, lit once its step has played
     * and is holding for the player; then Replay in Next's place, quiet until the whole thing has
     * played and lit after (a helper without steps shows it only then). A plate eases in when it
     * arrives and is gone at once when it is no longer the thing to press.
     */
    private poseActions = (t: number, stop?: number) => {
        const { replayAt } = this.layout
        const appear = (shownAt: number) => easeOutCubic(span(this.clock - shownAt, 0, ACTION_IN_MS))
        const showReplay = t >= replayAt || (!!this.nextButton && stop === undefined)
        if (showReplay && !this.replayButton.visible) this.replayShownAt = this.clock
        this.replayButton.visible = showReplay
        this.replayButton.alpha = appear(this.replayShownAt)
        this.replayButton.lit = easeOutCubic(span(t, replayAt, replayAt + NEXT_LIGHT_MS))
        this.replayButton.draw()
        if (this.backButton) {
            const show = this.manualStep > 0
            if (show && !this.backButton.visible) this.backShownAt = this.clock
            this.backButton.visible = show
            this.backButton.alpha = appear(this.backShownAt)
        }
        if (this.nextButton) {
            const show = stop !== undefined
            if (show && !this.nextButton.visible) this.nextShownAt = this.clock
            this.nextButton.visible = show
            this.nextButton.alpha = appear(this.nextShownAt)
            const held = show && t >= stop - 1
            if (held && this.heldAt === undefined) this.heldAt = this.clock
            if (!held) this.heldAt = undefined
            this.nextButton.lit = held ? easeOutCubic(span(this.clock - this.heldAt, 0, NEXT_LIGHT_MS)) : 0
            this.nextButton.draw()
        }
    }

    /** Each step lights when it is the step, and is stamped when it is done. */
    private drawTrack = (t: number) => {
        const { labels, litAt, doneAt, linkFill } = this.layout.steps
        const g = this.track.clear()
        const glow = this.trackGlow.clear()
        // The links first, so the diamonds sit over their ends.
        linkFill.forEach(([from, to], i) => {
            const x0 = this.trackX[i].end, x1 = this.trackX[i + 1].node - NODE_R - LINK_GAP
            if (x1 <= x0) return
            g.lineStyle(2, 0x000000, 0.55).moveTo(x0, TRACK_Y).lineTo(x1, TRACK_Y)
            g.lineStyle(1, RIM_LIGHT, 0.14).moveTo(x0, TRACK_Y + 1.5).lineTo(x1, TRACK_Y + 1.5)
            const fill = easeInOutCubic(span(t, from, to))
            if (fill > 0) {
                const head = lerp(x0, x1, fill)
                g.lineStyle({ width: 2, color: REWARD_GOLD, alpha: 1, cap: LINE_CAP.ROUND }).moveTo(x0, TRACK_Y).lineTo(head, TRACK_Y)
                // The bead of light leading the fill, gone once it arrives.
                if (fill < 1) glowDisc(glow, head, TRACK_Y, 7, REWARD_GOLD_HOT, 0.8, 3)
            }
        })
        g.lineStyle(0)
        labels.forEach((_, i) => {
            const cx = this.trackX[i].node, cy = TRACK_Y
            const label = this.trackLabels[i]
            const doneMs = t - doneAt[i]
            const hovered = this.hoveredStep === i
            // A step that can be picked answers the pointer with a bronze ring round its diamond.
            if (hovered) {
                g.lineStyle({ width: 1, color: RIM_LIGHT, alpha: 0.6, join: LINE_JOIN.MITER })
                diamond(g, cx, cy, NODE_R + 3.5)
                g.lineStyle(0)
            }
            if (doneMs >= 0) {
                const stamp = stampPose(doneMs)
                if (stamp.ringT < 1) {
                    g.lineStyle(1.25, REWARD_GOLD, 0.8 * (1 - stamp.ringT))
                    diamond(g, cx, cy, NODE_R + 1 + 11 * easeOutCubic(stamp.ringT))
                    g.lineStyle(0)
                }
                glowDisc(glow, cx, cy, NODE_R * 2.4, REWARD_GOLD, 0.5 * (1 - easeOutCubic(span(doneMs, 0, 600))), 3)
                sparkBurst(glow, cx, cy, stamp.sparkT, 8, NODE_R + 2, NODE_R + 13, REWARD_GOLD, i)
                filledSocket(g, cx, cy, NODE_R * stamp.socketScale, stamp.checkDraw)
                label.tint = lerpColor(RIM_LIGHT, REWARD_GOLD, stamp.labelGold)
                return
            }
            const lit = t >= litAt[i] ? easeOutCubic(span(t, litAt[i], litAt[i] + STEP_LIGHT_MS)) : 0
            if (lit > 0) {
                // The step being shown breathes, gently, so the eye finds it.
                const breathe = 0.5 - 0.5 * Math.cos(((t - litAt[i]) / 1400) * Math.PI * 2)
                glowDisc(glow, cx, cy, NODE_R * 2.6, REWARD_GOLD, (0.22 + 0.12 * breathe) * lit, 3)
            }
            g.lineStyle({ width: 1.25, color: lerpColor(PLOT_RIM, REWARD_GOLD, lit), alpha: 1, join: LINE_JOIN.MITER })
            g.beginFill(lerpColor(PLOT_FACE, 0x3a2810, lit), 0.95)
            diamond(g, cx, cy, NODE_R)
            g.endFill()
            g.lineStyle(0)
            if (lit > 0) {
                g.beginFill(REWARD_GOLD, 1)
                diamond(g, cx, cy, NODE_R * 0.42 * easeOutBack(span(t, litAt[i], litAt[i] + STEP_LIGHT_MS), 2.4))
                g.endFill()
            }
            label.tint = lerpColor(MUTED, INK, hovered ? Math.max(lit, 0.75) : lit)
        })
    }

    /**
     * A chunky arrow with a dark ink edge and a dropped shadow, squashed while it is held down -- or,
     * on a touch screen, a fingertip that presses in.
     */
    private drawPointer = (pose: DemoCursor) => {
        const g = this.pointer.clear()
        if (!(pose.alpha > 0)) return
        if (this.touch) {
            const r = pose.pressed ? 7.5 : 9
            g.beginFill(0x000000, 0.3 * pose.alpha).drawCircle(pose.x + 1.5, pose.y + 2, r).endFill()
            g.lineStyle({ width: 1.4, color: 0x1a120c, alpha: pose.alpha })
            g.beginFill(pose.pressed ? 0xfff0c8 : 0xffffff, 0.85 * pose.alpha).drawCircle(pose.x, pose.y, r).endFill()
            g.lineStyle(0)
            return
        }
        const s = pose.pressed ? 0.86 : 1
        const at = (dx: number, dy: number) => cursorArrow.map((v, i) => (i % 2 ? pose.y + dy + v * s : pose.x + dx + v * s))
        g.beginFill(0x000000, 0.35 * pose.alpha).drawPolygon(at(1.5, 2)).endFill()
        g.lineStyle({ width: 1.4, color: 0x1a120c, alpha: pose.alpha, join: LINE_JOIN.ROUND })
        g.beginFill(pose.pressed ? 0xfff0c8 : 0xffffff, pose.alpha).drawPolygon(at(0, 0)).endFill()
        g.lineStyle(0)
    }

    private updateCaption = ({ text, done }: { text: string; done: boolean }) => {
        if (this.caption.text !== text) {
            this.caption.text = text
            this.captionShownAt = this.clock
            // Leave room for whatever shares the row: always the step plates, or Replay once it is there.
            const controls = this.backButton ? ACTION_W + ACTION_GAP + ACTION_H : done ? ACTION_W : 0
            this.caption.style.wordWrapWidth = this.layout.contentWidth - (controls && controls + CAPTION_GAP)
        }
        const k = easeOutCubic(span(this.clock - this.captionShownAt, 0, CAPTION_IN_MS))
        this.caption.alpha = k
        this.caption.y = this.layout.captionY + 5 * (1 - k)
        this.caption.tint = done ? DONE_TINT : INK
    }

    destroy = () => {
        this.endDrag()
        if (this.hovered) this.hover(false)
        this.teardown()
        super.destroy({ children: true })
    }
}
